// Lightweight i18n + auth + axios helpers for the CRM app.
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token kept in localStorage (Phase 1 — Phase 4 will move to httpOnly cookies)
const TOKEN_KEY = "gtx_crm_token";
const ROLE_KEY = "gtx_crm_role";

export const auth = {
  setSession({ token, role }) {
    localStorage.setItem(TOKEN_KEY, token);
    if (role) localStorage.setItem(ROLE_KEY, role);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
  },
  token() {
    return localStorage.getItem(TOKEN_KEY) || "";
  },
  role() {
    return localStorage.getItem(ROLE_KEY) || "";
  },
  isAuthed() {
    return !!localStorage.getItem(TOKEN_KEY);
  },
  headers() {
    const t = this.token();
    return t ? { Authorization: `Bearer ${t}` } : {};
  },
};

export const crmApi = axios.create({ baseURL: `${API}/crm` });
crmApi.interceptors.request.use((cfg) => {
  const t = auth.token();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
crmApi.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e?.response?.status === 401) {
      auth.clear();
      if (!window.location.pathname.startsWith("/crm/login")) {
        window.location.href = "/crm/login";
      }
    }
    return Promise.reject(e);
  }
);

// Admin login (re-uses existing /admin/login endpoint)
export async function adminLogin(username, password) {
  const r = await axios.post(`${API}/admin/login`, { username, password });
  auth.setSession({ token: r.data.token, role: "admin" });
  return r.data;
}

export async function agentLogin(email, password) {
  const r = await axios.post(`${API}/crm/auth/login`, { email, password });
  auth.setSession({ token: r.data.token, role: "agent" });
  return r.data;
}

// ============ i18n ============
const dict = {
  es: {
    login: {
      title: "GTX CRM",
      subtitle: "Acceso para agentes",
      adminTab: "Admin",
      agentTab: "Agente",
      email: "Email",
      password: "Contraseña",
      username: "Usuario",
      submit: "Entrar",
      invalid: "Credenciales inválidas",
      backToWeb: "← Volver a la web",
    },
    nav: {
      dashboard: "Panel",
      leads: "Leads",
      pipeline: "Pipeline",
      tasks: "Tareas",
      agents: "Agentes",
      settings: "Ajustes",
      resources: "Recursos",
      timezones: "Zonas horarias",
      commissions: "Comisiones",
      callAssistant: "Asistente llamada",
      callStrategies: "Estrategias",
      agentActivity: "Actividad agentes",
      logout: "Salir",
    },
    stage: {
      not_interested: "No interesado",
      no_response: "No respondió",
      call_back: "Volver a contactar",
      new: "Nuevo",
      contacting: "Contactando",
      contacted: "Contactado",
      meeting_scheduled: "Reunión agendada",
      qualified: "Cualificado",
      proposal_sent: "Propuesta enviada",
      negotiation: "Negociación",
      won: "Ganado",
      lost: "Perdido",
    },
    dash: {
      newThisMonth: "Nuevos este mes",
      pipelineValue: "Valor pipeline",
      wonThisMonth: "Ganados este mes",
      convRate: "Tasa conversión",
      pendingTasks: "Tareas pendientes",
      overdueTasks: "Tareas vencidas",
      myGoal: "Mi objetivo mensual",
      agentRanking: "Ranking de agentes",
      stageDistribution: "Distribución por etapa",
      sourceDistribution: "Por origen",
      lostReasons: "Motivos de pérdida",
      noData: "Sin datos aún",
    },
    leads: {
      title: "Leads",
      newLead: "Nuevo lead",
      search: "Buscar empresa, contacto, email…",
      empty: "No hay leads que mostrar",
      assignedTo: "Asignado a",
      unassigned: "Sin asignar",
      bulkAssign: "Asignar a…",
      importCsv: "Importar CSV",
      exportCsv: "Exportar CSV",
      exportOk: "CSV descargado",
      reassignTitle: "Reasignar a otro agente",
      editNote: "Editar nota rápida",
      noteSaved: "Nota guardada",
      notePlaceholder: "Nota rápida: no responde, volver a llamar, interesado…",
      tagNoAnswer: "No responde",
      tagCallBack: "Volver a llamar",
      tagInterested: "Interesado",
      tagNotInterested: "No interesado",
      tagWrongNumber: "Nº equivocado",
      view: "Ver",
      delete: "Eliminar",
    },
    ghost: {
      title: "Modo fantasma · Pipeline de",
      subtitle: "Solo lectura · estás viendo el pipeline de otro agente",
      exit: "Salir",
      readonly: "Modo fantasma activo · solo lectura",
      viewPipelineBtn: "Ver pipeline",
    },
    zoho: {
      mailTooltip: "Abre tu cliente de correo predeterminado en modo Redactar con destinatario, asunto y cuerpo ya rellenados. Configura Zoho Mail como gestor de mailto: en Chrome una vez (chrome://settings/handlers) y siempre se abrirá en Zoho.",
      meetTooltip: "Abre Zoho Meetings para programar una reunión con este lead. Queda registrado en Activity.",
      emailSubject: "Seguimiento Global Tech Xperts",
      emailGreeting: "Hola",
      mailCopiedAndOpened: "Zoho Mail abierto · texto copiado al portapapeles (pega con Ctrl+V)",
      mailOpenedNoCopy: "Zoho Mail abierto",
      meetCopiedAndOpened: "Zoho Meetings abierto · datos copiados al portapapeles (pega con Ctrl+V)",
      meetOpenedNoCopy: "Zoho Meetings abierto",
    },
    assistant: {
      btn: "Asistente",
      tooltip: "Abre el asistente de llamada GTX con el guion paso a paso, objeciones, plantillas y la ficha post-llamada prerellenada con los datos de este lead.",
    },
    mailto: {
      tag: "Configuración única",
      title: "Haz que el botón 'Zoho Mail' abra siempre Zoho con el destinatario, asunto y cuerpo listos",
      subtitle: "Solo necesitas configurar Zoho Mail como gestor de mailto: en Chrome una vez. Después, todos los botones 'Zoho Mail' del CRM abrirán Zoho con todo prerellenado.",
      step1: "Abre Zoho Mail en Chrome (botón abajo).",
      step2: "En la barra de direcciones busca el icono ◆ a la derecha y haz clic — Chrome pedirá permiso para registrar Zoho como gestor de mailto:. Acepta.",
      step3: "Alternativa: chrome://settings/handlers → Permitir Zoho. Listo, todos los botones Zoho Mail del CRM funcionan ahora.",
      openZoho: "Abrir Zoho Mail",
      openHandlers: "Ajustes de Chrome",
      gotIt: "Entendido, no mostrar de nuevo",
    },
    call: {
      resultTitle: "¿Cómo fue la llamada?",
      hint: "Tu respuesta moverá el lead al stage correcto",
      r_contacted: "Hablé con el lead — sigamos",
      r_meetingScheduled: "Programó una reunión",
      r_callBack: "Pidió que le llame más tarde",
      r_noResponse: "No respondió / no contestó",
      r_notInterested: "No está interesado",
    },
    leadDetail: {
      back: "← Volver",
      company: "Empresa",
      contact: "Contacto",
      commercial: "Comercial",
      internal: "Interno",
      activity: "Actividad",
      tasks: "Tareas",
      save: "Guardar",
      saving: "Guardando…",
      saved: "Guardado",
      delete: "Eliminar lead",
      confirmDelete: "¿Eliminar este lead? Esta acción no se puede deshacer.",
      quickActions: "Acciones rápidas",
      noActivity: "Sin actividad registrada",
      addTask: "Añadir tarea",
    },
    tasks: {
      title: "Tareas",
      today: "Hoy",
      week: "Semana",
      all: "Todas",
      newTask: "Nueva tarea",
      done: "Hecho",
      undone: "Reabrir",
      overdue: "Vencida",
      noTasks: "Sin tareas",
      type: "Tipo",
      title2: "Título",
      due: "Fecha",
      description: "Descripción",
      addBtn: "Añadir",
      titlePh: "Llamar a Mohamed mañana 11:00…",
      dueDate: "Fecha y hora",
      descriptionOpt: "Notas (opcional)",
      descriptionPh: "Recordar mencionar la oferta GTX Europe",
      markDone: "Marcar como hecha",
      markPending: "Reabrir tarea",
      deleteConfirm: "¿Eliminar esta tarea?",
      created: "Tarea añadida",
      deleted: "Tarea eliminada",
      typeCall: "Llamada",
      typeEmail: "Email",
      typeWhatsapp: "WhatsApp",
      typeMeeting: "Reunión",
      typeProposal: "Propuesta",
      typeWait: "Esperar",
      typeNote: "Nota",
    },
    agents: {
      title: "Agentes",
      newAgent: "Nuevo agente",
      name: "Nombre",
      email: "Email",
      role: "Cargo",
      goal: "Objetivo €/mes",
      active: "Activo",
      created: "Creado",
      resetPw: "Reset contraseña",
      remove: "Eliminar",
      tempPwTitle: "Credenciales del agente",
      tempPwMsg: "Email enviado a {email}. Guarda esta contraseña por si acaso:",
      copy: "Copiar",
      copied: "Copiado",
    },
    common: {
      save: "Guardar",
      cancel: "Cancelar",
      close: "Cerrar",
      create: "Crear",
      edit: "Editar",
      delete: "Eliminar",
      yes: "Sí",
      no: "No",
      loading: "Cargando…",
      saving: "Guardando…",
      error: "Error",
      success: "Éxito",
      required: "Requerido",
      optional: "opcional",
      none: "Ninguno",
      search: "Buscar",
      all: "Todos",
      filters: "Filtros",
      back: "← Atrás",
      open: "Abrir",
    },
    chips: {
      unassigned: "sin asignar",
      pendingContact: "sin contactar",
      pendingQualify: "por calificar",
      selected: "seleccionados",
    },
    assign: {
      title: "Asignar leads a {name}",
      scopeUnassigned: "Sin asignar",
      scopeOther: "De otros agentes",
      scopeOwn: "Suyos",
      searchPlaceholder: "Buscar… o pega varios nombres separados por saltos de línea / comas",
      allCountries: "— Todos los países —",
      multiDetected: "{n} NOMBRES DETECTADOS — MODO MULTI-PASTE",
      multiSummary: "{matched} de {total} encontrados · {leads} leads coinciden en total",
      selectAllMatched: "Seleccionar los {n} matches",
      notFound: "Sin coincidencias en este scope",
      bulkBlockTitle: "SELECCIÓN MASIVA POR CANTIDAD",
      bulkBlockHint: "Aplica primero los filtros y luego toma los primeros N de la lista",
      take: "Tomar {n}",
      takeN: "Tomar N",
      selectAll: "seleccionar {n}",
      deselectAll: "deseleccionar todos",
      empty: "No hay leads que coincidan.",
      ownerChange: "cambia de dueño",
      assignN: "Asignar {n}",
      invalidNumber: "Introduce un número válido",
      selectedToast: "{n} lead(s) seleccionados",
      successToast: "{n} lead(s) asignados a {name}",
      assignedOk: "Lead asignado",
      reassignedOk: "Lead reasignado",
      alreadyAssignedHere: "Ya estaba asignado a este agente",
      confirmReassignPrefix: "Este lead ya está asignado a",
      confirmReassignSuffix: "¿Reasignar de todos modos? (fuerza mayor)",
      bulkAlreadyOther: "ya asignado(s) a OTRO agente",
      bulkAlreadyTarget: "ya en este agente (no-op)",
      bulkEligible: "sin asignar (listos)",
      bulkForceConfirm: "Pulsa OK para REASIGNAR todos por fuerza mayor.\nCancela para asignar solo los libres.",
      // Custom bulk-reassign dialog
      bulkConfirmTitle: "Confirmar reasignación masiva",
      bulkConfirmTo: "Destino:",
      bulkSampleHeader: "Leads ya asignados a otro agente (muestra):",
      bulkSampleMore: "más",
      bulkForce: "Reasignar TODOS (forzar)",
      bulkOnlyFree: "Asignar solo libres",
      bulkOnlyFreeHint: "Salta los leads que ya tienen otro agente",
      cancel: "Cancelar",
      selected: "seleccionados",
      assignBtn: "Asignar",
      clear: "Limpiar",
    },
    smartLoc: {
      countryPh: "Empieza a escribir un país…",
      cityLabel: "Ciudad (opcional)",
      cityPh: "Si el país no aparece",
      detected: "Zona horaria detectada",
      noneDetected: "Escribe un país o ciudad y la zona horaria se detectará automáticamente",
      ambiguous: "país amplio (verifica)",
      change: "Cambiar manualmente",
      pickManually: "Elegir manualmente",
      override: "Override manual",
    },
    agentsPage: {
      slug: "/equipo",
      subtitle: "{n} agentes · gestión de equipo + asignaciones",
      subtitleSingular: "{n} agente · gestión de equipo + asignaciones",
      created: "creado {date}",
      assign: "Asignar",
      role: "Rol",
      goal: "Objetivo",
      empty: "No hay agentes todavía",
      emptyHint: "Crea tu primer agente para empezar a asignarle leads.",
      emptyCta: "Crear agente",
      editTitle: "Editar · {name}",
      editFullName: "Nombre completo *",
      editPhone: "Teléfono",
      editJobTitle: "Cargo",
      editCountry: "País",
      editGoal: "Objetivo mensual (€)",
      editTimezone: "Zona horaria",
      editNotes: "Notas internas",
      tzSearchPlaceholder: "Filtrar (ej. madrid, tokyo, dubai…)",
      tzUndefined: "— sin definir —",
      tzSelected: "✓ seleccionada: {tz}",
      resetPwConfirm: "¿Resetear contraseña de {email}?",
      deleteConfirm: "¿Eliminar agente {name}?",
      deleted: "Agente eliminado",
      updated: "Agente actualizado",
    },
    resources: {
      slug: "/biblioteca",
      title: "Recursos",
      subtitle: "Documentos, videos y fuentes oficiales para el equipo · {n} {label}",
      labelSingular: "recurso",
      labelPlural: "recursos",
      new: "Nuevo recurso",
      empty: "Biblioteca vacía",
      emptyAdminHint: "Añade documentos, videos y enlaces oficiales para el equipo.",
      emptyAgentHint: "Tu admin aún no ha añadido recursos. Vuelve más tarde.",
      emptyCta: "Añadir primer recurso",
      searchPlaceholder: "Buscar título, descripción, tag…",
      formNewTitle: "Nuevo recurso",
      formEditTitle: "Editar recurso",
      fieldTitle: "Título *",
      fieldDescription: "Descripción",
      fieldUrl: "URL *",
      fieldUrlPlaceholder: "https://docs.google.com/... o https://youtube.com/...",
      fieldHtml: "Contenido HTML",
      fieldService: "Servicio GTX",
      htmlHint: "Se abrirá en una pestaña nueva del navegador del agente. Pega HTML completo (con <html>…</html>) o sólo el body.",
      popupBlocked: "El navegador bloqueó la ventana emergente. Permite popups para abrir el recurso.",
      fieldCategory: "Categoría",
      fieldType: "Tipo",
      fieldThumbnail: "URL de miniatura (opcional)",
      fieldTags: "Tags (separados por coma)",
      fieldTagsPlaceholder: "onboarding, frio, b2b",
      fieldPinned: "Fijar al inicio de su categoría",
      btnCreate: "Crear recurso",
      btnSave: "Guardar cambios",
      deleteConfirm: '¿Eliminar "{title}"?',
      deleted: "Recurso eliminado",
      updated: "Recurso actualizado",
      created: "Recurso creado",
      type: {
        document: "Documento",
        video: "Video",
        image: "Imagen",
        link: "Enlace",
        html: "HTML",
        other: "Otro",
      },
    },
    tz: {
      navLabel: "Timezone Manager",
      sublabel: "Centro de control horario · {a} agentes · {z} zonas de leads",
      hqLabel: "{city} · Gerencia",
      agentsBlockTitle: "Agentes de Venta",
      agentsBlockSubtitle: "{n} agentes registrados",
      zonesBlockTitle: "Zonas de Leads",
      zonesBlockSubtitle: "{n} zonas configuradas",
      plannerBlockTitle: "Meeting Planner",
      plannerBlockSubtitle: "Calcula ventanas comunes Gerencia + Agente + Lead",
      newAgent: "Nuevo agente",
      newZone: "Nueva zona",
      emptyAgents: "No hay agentes registrados aún",
      emptyZones: "No hay zonas de leads configuradas",
      yourZone: "Tu zona",
    },
    clockBar: {
      hq: "Amsterdam · Gerencia",
      yourZone: "Tu zona",
      lead: "Lead",
      removeLeadAria: "Quitar lead",
    },
    comm: {
      title: "Comisiones",
      subtitle: "Gestión de comisiones según el Acuerdo de Agente Comercial GTX",
      tabOverview: "Resumen",
      tabOperations: "Operaciones",
      tabSuppliers: "Proveedores",
      tabPayouts: "Pagos",
      tabCalculator: "Calculadora",
      kpiAccrued: "Devengado",
      kpiPaid: "Pagado",
      kpiPending: "Pendiente",
      kpiClawback: "Reversado",
      kpiThisMonth: "Devengado este mes",
      kpiSuppliers: "Proveedores activos",
      newOperation: "Nueva operación",
      newSupplier: "Nuevo proveedor",
      generatePayout: "Generar pago quincenal",
      colSupplier: "Proveedor",
      colCode: "Código GTX",
      colModality: "Modalidad",
      colUnits: "Unidades",
      colValue: "Valor (€)",
      colFee: "Fee GTX",
      colRate: "% Comisión",
      colAmount: "Comisión",
      colStatus: "Estado",
      colInvoiced: "Facturado",
      colCollected: "Cobrado",
      colActions: "Acciones",
      colCountry: "País",
      colAgent: "Agente",
      colFirstClose: "Primer cierre",
      colOps: "Operaciones",
      colPeriod: "Periodo",
      colTotal: "Total",
      colCreated: "Creado",
      modalityTech: "Tecnología",
      modalityGeneral: "General",
      statusPending: "Pendiente",
      statusAccrued: "Devengado",
      statusPaid: "Pagado",
      statusClawback: "Reversado",
      statusExpired: "Expirado",
      statusProspect: "Prospecto",
      statusActive: "Activo",
      statusGtxManaged: "Gestionado GTX",
      markCollected: "Marcar cobrado",
      doClawback: "Reversar",
      markPaid: "Marcar pagado",
      details: "Detalles",
      delete: "Eliminar",
      firstOpBadge: "1ª operación",
      residualBadge: "Residual",
      calcTitle: "Calculadora de comisión",
      calcDesc: "Previsualiza la comisión sobre el fee GTX según el Acuerdo. La comisión NO se calcula sobre el total facturado al cliente final.",
      modality: "Modalidad",
      units: "Unidades",
      operationValue: "Valor operación (€)",
      isFirst: "¿Primera operación con este proveedor?",
      daysSinceFirst: "Días desde el primer cierre",
      gtxFee: "Fee GTX",
      feePerUnit: "€/unidad",
      feePercentage: "% sobre valor",
      commissionRate: "% comisión aplicada",
      commissionAmount: "Comisión",
      supplierName: "Nombre del proveedor",
      supplierCountry: "País",
      supplierEmail: "Email de contacto",
      attribution: "Agente atribuido",
      notes: "Notas",
      noOperations: "No hay operaciones registradas",
      noSuppliers: "No has registrado proveedores",
      noPayouts: "No hay pagos generados",
      confirmDeleteOp: "¿Eliminar esta operación?",
      confirmClawback: "¿Confirmar reversión de comisión?",
      confirmMarkPaid: "¿Confirmar como pagado?",
      reasonClawback: "Motivo de la reversión",
      collectedDate: "Fecha de cobro",
      invoicedDate: "Fecha de facturación",
      operationCode: "Código GTX de operación",
      tierTech: "Tiers Tecnología (€/unidad)",
      tierGeneral: "Tiers General Merchandise (% sobre valor)",
      ruleFirst: "1ª operación: {pct}% sobre fee GTX",
      ruleResidual: "Operaciones siguientes (6 meses): {pct}% sobre fee GTX",
      ruleExpired: "Tras 6 meses · proveedor pasa a gestión GTX (0%)",
      ruleNonCollection: "Si no se cobra en 90 días → comisión no devenga",
      attributionLocked: "Atribución bloqueada al primer agente que registre formalmente",
      payoutPeriod: "Pagos quincenales · dentro de 15 días tras cobro",
      pickAgent: "Seleccionar agente",
      noAccruedForAgent: "Este agente no tiene operaciones devengadas para empaquetar",
      published: "Publicado",
      draft: "Borrador",
      publishedShort: "✓ pub.",
      draftShort: "borrador",
      publish: "Publicar",
      unpublish: "Despublicar",
      publishBulk: "Publicar todos los borradores",
      tabLedger: "Ledger",
      ledgerSubtitle: "Tiempo y dinero por agente · solo admin",
      colLedgerOps: "Ops",
      colDrafts: "Borradores",
      colMonthlyGoal: "Meta €/mes",
      colMonthProgress: "Mes actual",
      colLastActivity: "Última actividad",
      colFirstActivity: "Primera actividad",
      publishingHint: "Los borradores no son visibles para el agente hasta que los publiques.",
      draftFiltered: "Mostrar solo borradores",
      ledgerEmpty: "Sin agentes",
      fromLeadCta: "Crear comisión desde este lead",
      fromLeadHint: "Cliente ganado · genera un borrador de comisión",
      fromLeadNoSupplier: "Este agente no tiene proveedor registrado. Crea uno antes.",
      createdAsDraft: "Borrador de comisión creado. Revísalo y publícalo.",
      // Two-agent collaboration (Agreement v3 §4.6)
      pairedToggle: "Operación compartida entre dos agentes",
      pairedHint: "Al ganar el cliente, GTX generará dos comisiones espejo (una por agente) según el split configurado abajo.",
      prospectionAgent: "Agente de prospección",
      closingAgent: "Agente de cierre",
      pairedSameAgent: "Los dos agentes deben ser diferentes",
      splitPct: "Reparto (% para el prospector)",
      splitHint: "Por defecto 50/50. El cierre recibe el porcentaje restante.",
      pairedBadge: "50/50",
      pairedShareProspection: "Prospección",
      pairedShareClosing: "Cierre",
      pairedPartner: "Compartida con",
    },
    presence: {
      title: "Presencia",
      online: "en línea",
      offline: "desconectado",
      now: "ahora",
      empty: "Sin agentes",
      lastSeen: "Última actividad",
    },
    qual: {
      tab: "Cualificación",
      blockTitle: "Cualificación (cold call · 2-4 min)",
      blockSubtitle: "5 criterios. Sin selección = sin evaluar.",
      productType: "Tipo de producto",
      productTypeHighEnd: "Alta gama",
      productTypeAccessories: "Accesorios / gama baja",
      productTypeRequired: "Selecciona el tipo de producto para evaluar el volumen",
      criteriaPassed: "Pasa",
      criteriaFailed: "No pasa",
      criteriaPending: "Sin evaluar",
      noteHint: "Anota lo que dijo el lead",
      statusQualified: "Cualificado",
      statusDisqualified: "Descartado",
      statusPending: "Pendiente",
      scoreLabel: "{passed}/5 criterios pasa",
      reasonRequired: "Indica el motivo del descarte",
      disqualifiedReasonLabel: "Motivo de descarte",
      qualificationNotes: "Notas de cualificación",
      scheduleClosing: "Agendar closing call",
      scheduleClosingHint: "El lead avanza a 'Cualificado' en el pipeline",
      criteria: {
        sellsToEu: {
          label: "1. Vende a Europa",
          help: "Vende ya a UE o nombra país/cliente concreto en UE.",
        },
        volume: {
          label: "2. Volumen con valor real",
          help: "Alta gama: 60+ uds por pedido. Accesorios: pedido UE con valor relevante.",
          helpHighEnd: "Pasa si el pedido típico es de 60+ unidades.",
          helpAccessories: "Pasa solo si el pedido del cliente UE tiene valor económico relevante.",
          helpUnknown: "Selecciona primero el tipo de producto.",
        },
        decisionMaker: {
          label: "3. Es decisor",
          help: "Decide o influye directamente sobre la operativa europea.",
        },
        painPoint: {
          label: "4. Dolor que GTX resuelve",
          help: "Habla de IVA, cobro en EUR, aduanas o impago. NO pasa si dice 'no encuentro compradores'.",
        },
        prepayment: {
          label: "5. Cliente UE puede pagar por adelantado",
          help: "Paga al pedir o contra entrega. No pasa si exige 30-60 días.",
        },
      },
      messageQualified: "✓ Lead cualificado. Puedes agendar la closing call.",
      messageDisqualified: "Lead descartado. Queda archivado para nurturing.",
      messagePending: "Faltan criterios por evaluar.",
    },
  },
  en: {
    login: {
      title: "GTX CRM",
      subtitle: "Sales agents access",
      adminTab: "Admin",
      agentTab: "Agent",
      email: "Email",
      password: "Password",
      username: "Username",
      submit: "Sign in",
      invalid: "Invalid credentials",
      backToWeb: "← Back to website",
    },
    nav: {
      dashboard: "Dashboard",
      leads: "Leads",
      pipeline: "Pipeline",
      tasks: "Tasks",
      agents: "Agents",
      settings: "Settings",
      resources: "Resources",
      timezones: "Timezones",
      commissions: "Commissions",
      callAssistant: "Call assistant",
      callStrategies: "Call strategies",
      agentActivity: "Agent activity",
      logout: "Sign out",
    },
    stage: {
      not_interested: "Not interested",
      no_response: "No response",
      call_back: "Call back",
      new: "New",
      contacting: "Contacting",
      contacted: "Contacted",
      meeting_scheduled: "Meeting scheduled",
      qualified: "Qualified",
      proposal_sent: "Proposal sent",
      negotiation: "Negotiation",
      won: "Won",
      lost: "Lost",
    },
    dash: {
      newThisMonth: "New this month",
      pipelineValue: "Pipeline value",
      wonThisMonth: "Won this month",
      convRate: "Conversion rate",
      pendingTasks: "Pending tasks",
      overdueTasks: "Overdue tasks",
      myGoal: "My monthly goal",
      agentRanking: "Agent ranking",
      stageDistribution: "By stage",
      sourceDistribution: "By source",
      lostReasons: "Lost reasons",
      noData: "No data yet",
    },
    leads: {
      title: "Leads",
      newLead: "New lead",
      search: "Search company, contact, email…",
      empty: "No leads to show",
      assignedTo: "Assigned to",
      unassigned: "Unassigned",
      bulkAssign: "Assign to…",
      importCsv: "Import CSV",
      exportCsv: "Export CSV",
      exportOk: "CSV downloaded",
      reassignTitle: "Reassign to another agent",
      editNote: "Edit quick note",
      noteSaved: "Note saved",
      notePlaceholder: "Quick note: no answer, call back, interested…",
      tagNoAnswer: "No answer",
      tagCallBack: "Call back",
      tagInterested: "Interested",
      tagNotInterested: "Not interested",
      tagWrongNumber: "Wrong number",
      view: "View",
      delete: "Delete",
    },
    ghost: {
      title: "Ghost mode · Pipeline of",
      subtitle: "Read-only · you are viewing another agent's pipeline",
      exit: "Exit",
      readonly: "Ghost mode active · read-only",
      viewPipelineBtn: "View pipeline",
    },
    zoho: {
      mailTooltip: "Opens your default mail client in compose mode with recipient, subject and body pre-filled. Set Zoho Mail as the mailto: handler in Chrome once (chrome://settings/handlers) and it will always open in Zoho.",
      meetTooltip: "Opens Zoho Meetings to schedule a meeting with this lead. Logged in Activity.",
      emailSubject: "Global Tech Xperts follow-up",
      emailGreeting: "Hi",
      mailCopiedAndOpened: "Zoho Mail opened · text copied to clipboard (paste with Ctrl+V)",
      mailOpenedNoCopy: "Zoho Mail opened",
      meetCopiedAndOpened: "Zoho Meetings opened · details copied to clipboard (paste with Ctrl+V)",
      meetOpenedNoCopy: "Zoho Meetings opened",
    },
    assistant: {
      btn: "Assistant",
      tooltip: "Opens the GTX call assistant with the step-by-step script, objections, templates and the post-call card pre-filled with this lead's data.",
    },
    mailto: {
      tag: "One-time setup",
      title: "Make the 'Zoho Mail' button always open Zoho with recipient, subject and body ready",
      subtitle: "You only need to set Zoho Mail as the mailto: handler in Chrome once. After that, every 'Zoho Mail' button in the CRM opens Zoho with everything pre-filled.",
      step1: "Open Zoho Mail in Chrome (button below).",
      step2: "In the address bar look for the ◆ icon on the right and click it — Chrome will ask to register Zoho as the mailto: handler. Accept.",
      step3: "Alternative: chrome://settings/handlers → Allow Zoho. Done — every Zoho Mail button in the CRM now works.",
      openZoho: "Open Zoho Mail",
      openHandlers: "Chrome settings",
      gotIt: "Got it, don't show again",
    },
    call: {
      resultTitle: "How did the call go?",
      hint: "Your answer moves the lead to the right stage",
      r_contacted: "I talked with the lead — moving on",
      r_meetingScheduled: "They scheduled a meeting",
      r_callBack: "They asked me to call back later",
      r_noResponse: "No response / didn't pick up",
      r_notInterested: "They are not interested",
    },
    leadDetail: {
      back: "← Back",
      company: "Company",
      contact: "Contact",
      commercial: "Commercial",
      internal: "Internal",
      activity: "Activity",
      tasks: "Tasks",
      save: "Save",
      saving: "Saving…",
      saved: "Saved",
      delete: "Delete lead",
      confirmDelete: "Delete this lead? This cannot be undone.",
      quickActions: "Quick actions",
      noActivity: "No activity yet",
      addTask: "Add task",
    },
    tasks: {
      title: "Tasks",
      today: "Today",
      week: "Week",
      all: "All",
      newTask: "New task",
      done: "Done",
      undone: "Reopen",
      overdue: "Overdue",
      noTasks: "No tasks",
      type: "Type",
      title2: "Title",
      due: "Due",
      description: "Description",
      addBtn: "Add",
      titlePh: "Call Mohamed tomorrow at 11am…",
      dueDate: "Date & time",
      descriptionOpt: "Notes (optional)",
      descriptionPh: "Remember to mention the GTX Europe offer",
      markDone: "Mark as done",
      markPending: "Reopen task",
      deleteConfirm: "Delete this task?",
      created: "Task added",
      deleted: "Task deleted",
      typeCall: "Call",
      typeEmail: "Email",
      typeWhatsapp: "WhatsApp",
      typeMeeting: "Meeting",
      typeProposal: "Proposal",
      typeWait: "Wait",
      typeNote: "Note",
    },
    agents: {
      title: "Agents",
      newAgent: "New agent",
      name: "Name",
      email: "Email",
      role: "Role",
      goal: "Goal €/mo",
      active: "Active",
      created: "Created",
      resetPw: "Reset password",
      remove: "Remove",
      tempPwTitle: "Agent credentials",
      tempPwMsg: "Email sent to {email}. Keep this password just in case:",
      copy: "Copy",
      copied: "Copied",
    },
    common: {
      save: "Save",
      cancel: "Cancel",
      close: "Close",
      create: "Create",
      edit: "Edit",
      delete: "Delete",
      yes: "Yes",
      no: "No",
      loading: "Loading…",
      saving: "Saving…",
      error: "Error",
      success: "Success",
      required: "Required",
      optional: "optional",
      none: "None",
      search: "Search",
      all: "All",
      filters: "Filters",
      back: "← Back",
      open: "Open",
    },
    chips: {
      unassigned: "unassigned",
      pendingContact: "to contact",
      pendingQualify: "to qualify",
      selected: "selected",
    },
    assign: {
      title: "Assign leads to {name}",
      scopeUnassigned: "Unassigned",
      scopeOther: "From other agents",
      scopeOwn: "Theirs",
      searchPlaceholder: "Search… or paste multiple names separated by newlines / commas",
      allCountries: "— All countries —",
      multiDetected: "{n} NAMES DETECTED — MULTI-PASTE MODE",
      multiSummary: "{matched} of {total} found · {leads} matching leads total",
      selectAllMatched: "Select all {n} matches",
      notFound: "Not found in this scope",
      bulkBlockTitle: "BULK SELECT BY QUANTITY",
      bulkBlockHint: "Apply filters first, then take the first N from the list",
      take: "Take {n}",
      takeN: "Take N",
      selectAll: "select {n}",
      deselectAll: "deselect all",
      empty: "No leads match.",
      ownerChange: "owner changes",
      assignN: "Assign {n}",
      invalidNumber: "Enter a valid number",
      selectedToast: "{n} lead(s) selected",
      successToast: "{n} lead(s) assigned to {name}",
      assignedOk: "Lead assigned",
      reassignedOk: "Lead reassigned",
      alreadyAssignedHere: "Was already assigned to this agent",
      confirmReassignPrefix: "This lead is already assigned to",
      confirmReassignSuffix: "Reassign anyway? (force majeure)",
      bulkAlreadyOther: "already assigned to ANOTHER agent",
      bulkAlreadyTarget: "already on this agent (no-op)",
      bulkEligible: "unassigned (ready)",
      bulkForceConfirm: "OK to FORCE-REASSIGN everything.\nCancel to assign only the free ones.",
      // Custom bulk-reassign dialog
      bulkConfirmTitle: "Confirm bulk reassignment",
      bulkConfirmTo: "Target:",
      bulkSampleHeader: "Leads already assigned to another agent (sample):",
      bulkSampleMore: "more",
      bulkForce: "Reassign ALL (force)",
      bulkOnlyFree: "Assign only free ones",
      bulkOnlyFreeHint: "Skips leads already owned by another agent",
      cancel: "Cancel",
      selected: "selected",
      assignBtn: "Assign",
      clear: "Clear",
    },
    smartLoc: {
      countryPh: "Start typing a country…",
      cityLabel: "City (optional)",
      cityPh: "If the country isn't listed",
      detected: "Detected timezone",
      noneDetected: "Type a country or city and the timezone will be auto-detected",
      ambiguous: "broad country (verify)",
      change: "Change manually",
      pickManually: "Pick manually",
      override: "Manual override",
    },
    agentsPage: {
      slug: "/team",
      subtitle: "{n} agents · team management + assignments",
      subtitleSingular: "{n} agent · team management + assignments",
      created: "created {date}",
      assign: "Assign",
      role: "Role",
      goal: "Goal",
      empty: "No agents yet",
      emptyHint: "Create your first agent to start assigning leads.",
      emptyCta: "Create agent",
      editTitle: "Edit · {name}",
      editFullName: "Full name *",
      editPhone: "Phone",
      editJobTitle: "Job title",
      editCountry: "Country",
      editGoal: "Monthly goal (€)",
      editTimezone: "Timezone",
      editNotes: "Internal notes",
      tzSearchPlaceholder: "Filter (e.g. madrid, tokyo, dubai…)",
      tzUndefined: "— not set —",
      tzSelected: "✓ selected: {tz}",
      resetPwConfirm: "Reset password for {email}?",
      deleteConfirm: "Delete agent {name}?",
      deleted: "Agent deleted",
      updated: "Agent updated",
    },
    resources: {
      slug: "/library",
      title: "Resources",
      subtitle: "Docs, videos and official sources for the team · {n} {label}",
      labelSingular: "resource",
      labelPlural: "resources",
      new: "New resource",
      empty: "Empty library",
      emptyAdminHint: "Add documents, videos and official links for the team.",
      emptyAgentHint: "Your admin has not added any resources yet. Check back later.",
      emptyCta: "Add first resource",
      searchPlaceholder: "Search title, description, tag…",
      formNewTitle: "New resource",
      formEditTitle: "Edit resource",
      fieldTitle: "Title *",
      fieldDescription: "Description",
      fieldUrl: "URL *",
      fieldUrlPlaceholder: "https://docs.google.com/... or https://youtube.com/...",
      fieldHtml: "HTML content",
      fieldService: "GTX Service",
      htmlHint: "Opens in a new browser tab for the agent. Paste full HTML (with <html>…</html>) or just the body.",
      popupBlocked: "Browser blocked the popup. Allow popups to open the resource.",
      fieldCategory: "Category",
      fieldType: "Type",
      fieldThumbnail: "Thumbnail URL (optional)",
      fieldTags: "Tags (comma-separated)",
      fieldTagsPlaceholder: "onboarding, cold, b2b",
      fieldPinned: "Pin to top of its category",
      btnCreate: "Create resource",
      btnSave: "Save changes",
      deleteConfirm: 'Delete "{title}"?',
      deleted: "Resource deleted",
      updated: "Resource updated",
      created: "Resource created",
      type: {
        document: "Document",
        video: "Video",
        image: "Image",
        link: "Link",
        html: "HTML",
        other: "Other",
      },
    },
    tz: {
      navLabel: "Timezone Manager",
      sublabel: "Time control hub · {a} agents · {z} lead zones",
      hqLabel: "{city} · HQ",
      agentsBlockTitle: "Sales Agents",
      agentsBlockSubtitle: "{n} registered agents",
      zonesBlockTitle: "Lead Zones",
      zonesBlockSubtitle: "{n} zones configured",
      plannerBlockTitle: "Meeting Planner",
      plannerBlockSubtitle: "Find common windows HQ + Agent + Lead",
      newAgent: "New agent",
      newZone: "New zone",
      emptyAgents: "No agents registered yet",
      emptyZones: "No lead zones configured",
      yourZone: "Your zone",
    },
    clockBar: {
      hq: "Amsterdam · HQ",
      yourZone: "Your zone",
      lead: "Lead",
      removeLeadAria: "Remove lead",
    },
    comm: {
      title: "Commissions",
      subtitle: "Commission management per the GTX Commercial Agent Agreement",
      tabOverview: "Overview",
      tabOperations: "Operations",
      tabSuppliers: "Suppliers",
      tabPayouts: "Payouts",
      tabCalculator: "Calculator",
      kpiAccrued: "Accrued",
      kpiPaid: "Paid",
      kpiPending: "Pending",
      kpiClawback: "Clawback",
      kpiThisMonth: "Accrued this month",
      kpiSuppliers: "Active suppliers",
      newOperation: "New operation",
      newSupplier: "New supplier",
      generatePayout: "Generate bi-weekly payout",
      colSupplier: "Supplier",
      colCode: "GTX code",
      colModality: "Modality",
      colUnits: "Units",
      colValue: "Value (€)",
      colFee: "GTX fee",
      colRate: "Commission %",
      colAmount: "Commission",
      colStatus: "Status",
      colInvoiced: "Invoiced",
      colCollected: "Collected",
      colActions: "Actions",
      colCountry: "Country",
      colAgent: "Agent",
      colFirstClose: "First close",
      colOps: "Operations",
      colPeriod: "Period",
      colTotal: "Total",
      colCreated: "Created",
      modalityTech: "Technology",
      modalityGeneral: "General",
      statusPending: "Pending",
      statusAccrued: "Accrued",
      statusPaid: "Paid",
      statusClawback: "Clawback",
      statusExpired: "Expired",
      statusProspect: "Prospect",
      statusActive: "Active",
      statusGtxManaged: "GTX-managed",
      markCollected: "Mark collected",
      doClawback: "Clawback",
      markPaid: "Mark paid",
      details: "Details",
      delete: "Delete",
      firstOpBadge: "1st op",
      residualBadge: "Residual",
      calcTitle: "Commission calculator",
      calcDesc: "Preview commission on the GTX fee per the Agreement. Commission is NOT calculated on the total invoiced to the European client.",
      modality: "Modality",
      units: "Units",
      operationValue: "Operation value (€)",
      isFirst: "Is this the first operation with this supplier?",
      daysSinceFirst: "Days since first close",
      gtxFee: "GTX fee",
      feePerUnit: "€/unit",
      feePercentage: "% on value",
      commissionRate: "Applied commission %",
      commissionAmount: "Commission",
      supplierName: "Supplier name",
      supplierCountry: "Country",
      supplierEmail: "Contact email",
      attribution: "Attributed agent",
      notes: "Notes",
      noOperations: "No operations recorded",
      noSuppliers: "You have no registered suppliers",
      noPayouts: "No payouts generated",
      confirmDeleteOp: "Delete this operation?",
      confirmClawback: "Confirm commission clawback?",
      confirmMarkPaid: "Confirm as paid?",
      reasonClawback: "Clawback reason",
      collectedDate: "Collection date",
      invoicedDate: "Invoiced date",
      operationCode: "GTX operation code",
      tierTech: "Technology tiers (€/unit)",
      tierGeneral: "General Merchandise tiers (% on value)",
      ruleFirst: "1st operation: {pct}% on GTX fee",
      ruleResidual: "Subsequent operations (6 months): {pct}% on GTX fee",
      ruleExpired: "After 6 months · supplier transitions to GTX-managed (0%)",
      ruleNonCollection: "If uncollected in 90 days → commission does NOT accrue",
      attributionLocked: "Attribution locked to the first agent who formally registers the supplier",
      payoutPeriod: "Bi-weekly payouts · within 15 days after collection",
      pickAgent: "Pick an agent",
      noAccruedForAgent: "This agent has no accrued operations to bundle",
      published: "Published",
      draft: "Draft",
      publishedShort: "✓ pub.",
      draftShort: "draft",
      publish: "Publish",
      unpublish: "Unpublish",
      publishBulk: "Publish all drafts",
      tabLedger: "Ledger",
      ledgerSubtitle: "Time & money per agent · admin only",
      colLedgerOps: "Ops",
      colDrafts: "Drafts",
      colMonthlyGoal: "Goal €/mo",
      colMonthProgress: "This month",
      colLastActivity: "Last activity",
      colFirstActivity: "First activity",
      publishingHint: "Drafts are hidden from the agent until you publish them.",
      draftFiltered: "Show only drafts",
      ledgerEmpty: "No agents",
      fromLeadCta: "Create commission from this lead",
      fromLeadHint: "Deal won · generates a commission draft",
      fromLeadNoSupplier: "This agent has no registered supplier. Create one first.",
      createdAsDraft: "Commission draft created. Review and publish.",
      // Two-agent collaboration (Agreement v3 §4.6)
      pairedToggle: "Operation split between two agents",
      pairedHint: "When the client is won, GTX will generate two mirrored commission entries (one per agent) using the split configured below.",
      prospectionAgent: "Prospection agent",
      closingAgent: "Closing agent",
      pairedSameAgent: "Both agents must be different",
      splitPct: "Split (% for the prospector)",
      splitHint: "Default 50/50. The closer receives the remaining percentage.",
      pairedBadge: "50/50",
      pairedShareProspection: "Prospection",
      pairedShareClosing: "Closing",
      pairedPartner: "Shared with",
    },
    presence: {
      title: "Presence",
      online: "online",
      offline: "offline",
      now: "now",
      empty: "No agents",
      lastSeen: "Last activity",
    },
    qual: {
      tab: "Qualification",
      blockTitle: "Qualification (cold call · 2-4 min)",
      blockSubtitle: "5 criteria. No selection = not evaluated.",
      productType: "Product type",
      productTypeHighEnd: "High-end",
      productTypeAccessories: "Accessories / low-end",
      productTypeRequired: "Select product type to evaluate volume",
      criteriaPassed: "Pass",
      criteriaFailed: "Fail",
      criteriaPending: "Not evaluated",
      noteHint: "Note what the lead said",
      statusQualified: "Qualified",
      statusDisqualified: "Disqualified",
      statusPending: "Pending",
      scoreLabel: "{passed}/5 criteria pass",
      reasonRequired: "Provide a disqualification reason",
      disqualifiedReasonLabel: "Disqualification reason",
      qualificationNotes: "Qualification notes",
      scheduleClosing: "Schedule closing call",
      scheduleClosingHint: "Lead moves to 'Qualified' in the pipeline",
      criteria: {
        sellsToEu: {
          label: "1. Sells to Europe",
          help: "Already sells to EU or names a specific EU country/client.",
        },
        volume: {
          label: "2. Real volume",
          help: "High-end: 60+ units/order. Accessories: EU order with relevant value.",
          helpHighEnd: "Pass if a typical order is 60+ units.",
          helpAccessories: "Pass only if the EU client order has relevant economic value.",
          helpUnknown: "Pick a product type first.",
        },
        decisionMaker: {
          label: "3. Decision maker",
          help: "Decides or directly influences EU operations.",
        },
        painPoint: {
          label: "4. Pain GTX solves",
          help: "Mentions VAT, EUR collection, customs or non-payment. FAIL if 'I can't find buyers'.",
        },
        prepayment: {
          label: "5. EU client can prepay",
          help: "Pays upfront or on delivery. Fails if they demand 30-60 days.",
        },
      },
      messageQualified: "✓ Lead qualified. You can schedule the closing call.",
      messageDisqualified: "Lead disqualified. Archived for nurturing.",
      messagePending: "Some criteria are still pending.",
    },
  },
};

export function tCrm(lang, key) {
  const parts = key.split(".");
  let node = dict[lang] || dict.es;
  for (const p of parts) {
    node = node?.[p];
    if (node === undefined) return key;
  }
  return node;
}

// Same as tCrm but interpolates {placeholders} from a vars object.
// Example: tFmt("es", "assign.title", { name: "Maria" })
export function tFmt(lang, key, vars = {}) {
  const s = tCrm(lang, key);
  if (typeof s !== "string") return s;
  return s.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}

export const STAGES = [
  "new",
  "contacting",
  "no_response",
  "call_back",
  "contacted",
  "meeting_scheduled",
  "qualified",
  "proposal_sent",
  "negotiation",
  "won",
  "not_interested",
];
export const STAGE_COLORS = {
  not_interested: "#b91c1c",
  no_response: "#71717a",
  call_back: "#eab308",
  new: "#64748b",
  contacting: "#3b82f6",
  contacted: "#6366f1",
  meeting_scheduled: "#8b5cf6",
  qualified: "#a855f7",
  proposal_sent: "#ec4899",
  negotiation: "#f59e0b",
  won: "#10b981",
  lost: "#ef4444",
};

export const TEMPERATURE_META = {
  cold: { color: "#3b82f6", icon: "❄️", label_es: "Frío", label_en: "Cold" },
  warm: { color: "#eab308", icon: "🌤️", label_es: "Tibio", label_en: "Warm" },
  hot: { color: "#f97316", icon: "🔥", label_es: "Caliente", label_en: "Hot" },
  very_hot: { color: "#ef4444", icon: "⚡", label_es: "Muy caliente", label_en: "Very hot" },
};

export const TASK_TYPE_META = {
  call: { icon: "📞", color: "#3b82f6" },
  email: { icon: "✉️", color: "#6366f1" },
  whatsapp: { icon: "💬", color: "#10b981" },
  meeting: { icon: "📅", color: "#8b5cf6" },
  proposal: { icon: "📄", color: "#f59e0b" },
  wait: { icon: "⏳", color: "#94a3b8" },
  note: { icon: "📝", color: "#64748b" },
};

export const LEAD_SOURCES = ["Web", "Referral", "LinkedIn", "Event", "Email", "Cold call", "Ad", "Other"];
export const INDUSTRIES = ["Distribution", "Retail", "E-commerce", "Telecom", "Repair shop", "Education", "Healthcare", "Manufacturing", "Other"];
export const COMPANY_SIZES = ["1-10", "10-50", "50-200", "200-500", "500+"];
export const CURRENCIES = ["EUR", "USD", "GBP", "MXN", "ARS"];
export const URGENCIES = ["high", "medium", "low"];
export const LOST_REASONS = ["price", "timing", "competition", "not_interested", "ghosted", "not_ideal", "other"];

export function formatCurrency(value, currency = "EUR") {
  if (!value) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

// Dev-only logger so silent network failures still leave a trace in development
// without polluting the production console.
export function logDevError(scope, err) {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error(`[${scope}]`, err);
  }
}
